import { useParams, useRouteContext } from "@tanstack/react-router";
import { useState } from "react";
import { Tab, Tabs } from "react-bootstrap";

import LayoutContentArea from "@/components/layout/LayoutContentArea";
import ChildIndividualHeading from "@/components/records/child/ChildIndividualHeading";
import ATTNRecordsTab from "@/components/records/child/ATTNRecordsTab";
import CHLDRecordsTab from "@/components/records/child/CHLDRecordsTab";
import CLENRecordsTab from "@/components/records/child/CLENRecordsTab";
import ENRLRecordsTab from "@/components/records/child/ENRLRecordsTab";
import FAMIRecordsTab from "@/components/records/child/FAMIRecordsTab";
import LANGRecordsTab from "@/components/records/child/LANGRecordsTab";
import PRGMRecordsTab from "@/components/records/child/PRGMRecordsTab";
import SLNGRecordsTab from "@/components/records/child/SLNGRecordsTab";
import { useLeaData } from "@/queries.ts";
import Breadcrumb from "@/components/shared/BreadCrumb";

export default function ChildPage() {
  const leaContext = useRouteContext({ from: "/$leaid" });

  const params = useParams({
    from: "/$leaid/_error-wrapper/records/child/$entityid",
  });
  const csppid = params.entityid as string;
  const leaid = params.leaid as string;

  // Get data for the classroom, staff, and enrollment records associated with the classroom
  const leaData = useLeaData(leaid)?.data;

  const chldRecords =
    leaData?.entities?.children?.[params.entityid]?.records?.map((record) => ({
      ...record,
      PreschoolName: leaContext?.currentOrgPreschools?.find(
        (x) => x.PreschoolCDSCode === record.PreschoolCode
      )?.PreschoolName,
    })) ?? [];

  chldRecords.sort((x, y) => y.ProgramYear.localeCompare(x.ProgramYear));

  const child = chldRecords[0];

  if (!child) {
    throw new Error(`No child records exist for this CSPPID`);
  }

  const famiRecords = child.FamilyRecords ?? [];
  const langRecords = child.LangRecords ?? [];
  const slngRecords = child.SLngRecords ?? [];
  const enrlRecords = child.Enrollments ?? [];
  const clenRecords = child.ClassroomAssignments ?? [];
  const prgmRecords = child.EducationPrograms ?? [];

  // Construct child's name using correct property names
  const childName =
    child.ChildFirstName && child.ChildLastName
      ? `${child.ChildFirstName} ${child.ChildLastName}`
      : child.ChildFirstName || child.ChildLastName || `Child ${csppid}`;

  const breadcrumbItems = [
    {
      label: "Home",
      to: "/$leaid" as const,
      params: { leaid },
      ariaLabel: "Go to Home",
      isHome: true,
    },
    {
      label: "Child Records",
      to: "/$leaid/records/child" as const,
      params: { leaid },
    },
    {
      label: childName,
    },
  ];

  // Manage tabs state to render & size content only when the tab is active
  const [activeTab, setActiveTab] = useState("chld");

  return (
    <LayoutContentArea
      headingText={null}
      noBorder={true}
      showHeadingSection={false}
    >
      <Breadcrumb items={breadcrumbItems} />

      <div
        className="card rounded-4 border-0 mb-4"
        style={{
          boxShadow:
            "0 1px 3px 0 rgb(15 23 42 / 0.08), 0 1px 2px -1px rgb(15 23 42 / 0.06)",
        }}
      >
        <div className="card-custom shadow-none p-0">
          <div className="p-3 p-md-4">
            <ChildIndividualHeading />
          </div>

          {/* Find alternate solution to grid size calculations that doesn't require re-rendering whole tab contents each time tab changes */}
          <Tabs
            className="section-tabs"
            fill
            activeKey={activeTab}
            onSelect={(key) => setActiveTab(key || "")}
          >
            {leaContext.hasRole("CHLDView") && (
              <Tab eventKey="chld" title="Child (CHLD)" className="p-4">
                {activeTab === "chld" && (
                  <CHLDRecordsTab
                    chldRecords={chldRecords}
                    famiRecords={famiRecords}
                    langRecords={langRecords}
                    slngRecords={slngRecords}
                    enrlRecords={enrlRecords}
                    clenRecords={clenRecords}
                  />
                )}
              </Tab>
            )}
            {leaContext.hasRole("FAMIView") && (
              <Tab eventKey="fami" title="Family (FAMI)" className="p-4">
                {activeTab === "fami" && (
                  <FAMIRecordsTab famiRecords={famiRecords} child={child} />
                )}
              </Tab>
            )}
            {leaContext.hasRole("LANGView") && (
              <Tab eventKey="lang" title="Language (LANG)" className="p-4">
                {activeTab === "lang" && (
                  <LANGRecordsTab langRecords={langRecords} child={child} />
                )}
              </Tab>
            )}
            {leaContext.hasRole("SLNGView") && (
              <Tab
                eventKey="slng"
                title="Supplemental Language (SLNG)"
                className="p-4"
              >
                {activeTab === "slng" && (
                  <SLNGRecordsTab slngRecords={slngRecords} child={child} />
                )}
              </Tab>
            )}
            {leaContext.hasRole("ENRLView") && (
              <Tab eventKey="enrl" title="Enrollment (ENRL)" className="p-4">
                {activeTab === "enrl" && (
                  <ENRLRecordsTab enrlRecords={enrlRecords} child={child} />
                )}
              </Tab>
            )}
            {leaContext.hasRole("CLENView") && (
              <Tab
                eventKey="clen"
                title="Classroom Enrollment (CLEN)"
                className="p-4"
              >
                {activeTab === "clen" && (
                  <CLENRecordsTab clenRecords={clenRecords} child={child} />
                )}
              </Tab>
            )}
            {leaContext.hasRole("ATTNView") && (
              <Tab eventKey="attn" title="Attendance (ATTN)" className="p-4">
                {activeTab === "attn" && (
                  <ATTNRecordsTab
                    attnRecords={child.Attendance ?? []}
                    enrlRecords={enrlRecords}
                    child={child}
                  />
                )}
              </Tab>
            )}
            {leaContext.hasRole("PRGMView") && (
              <Tab
                eventKey="prgm"
                title="Education Programs (PRGM)"
                className="p-4"
              >
                {activeTab === "prgm" && (
                  <PRGMRecordsTab prgmRecords={prgmRecords} child={child} />
                )}
              </Tab>
            )}
          </Tabs>
        </div>
      </div>
    </LayoutContentArea>
  );
}
